import {
  Body1,
  Button,
  Card,
  CardHeader,
  FlatTree,
  FlatTreeItem,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  mergeClasses,
  Text,
  Tooltip,
  TreeItemLayout,
  useHeadlessFlatTree_unstable,
  type HeadlessFlatTreeItemProps,
} from '@fluentui/react-components'
import { Eye16Regular, MoreHorizontal16Regular, Open16Regular } from '@fluentui/react-icons'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTooltipControl } from '../../hooks/useTooltipControl'
import { useStyles } from './FileCard.style'
import { FileCardTooltipContent } from './FileCardTooltipContent'

// value from HeadlessFlatTreeItemProps is a number, but we need a string
export type BraveFlatTreeListItem = Omit<HeadlessFlatTreeItemProps, 'value'> & {
  value: string
  title: string
  versionId?: string
  version?: string
  createdAt?: string
  updatedAt?: string
  documentUrl?: string
}

interface BraveFileCardProps {
  items: BraveFlatTreeListItem[]
  onChange: (parentId: string, selectedIds: string[]) => void
}

const SELECTION_MODE = 'multiselect'

const BraveFileCard = ({ items, onChange }: BraveFileCardProps) => {
  const styles = useStyles()
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [isExpanded, setIsExpanded] = useState(false)
  const [openSectionMenuId, setOpenSectionMenuId] = useState<string | null>(null)
  const {
    visibleTooltip,
    handleMouseEnter,
    handleMouseLeave,
    handleFocus,
    handleBlur,
    clearTooltip,
    tooltipContentClassName,
  } = useTooltipControl()

  // As opposed to the FileCard, here we use uncontrolled Tree behaviour
  // We track only selected values given that the initial state is always empty
  const flatTree = useHeadlessFlatTree_unstable(items, {
    selectionMode: SELECTION_MODE,
    onCheckedChange: (_, data) => {
      // ids selected for the purpose of BraveFileCard (includes files and folders)
      const selectedIdsList: string[] = []
      data?.checkedItems.forEach((value, key) => {
        // flow for checked items, value would be truthy
        if (value) {
          selectedIdsList.push(key as string)
        }
      })

      onChange(items[0]?.value, selectedIdsList)
      setSelectedIds(selectedIdsList)
    },
  })

  const { t } = useTranslation()

  return (
    <Card className={styles.card}>
      <FlatTree
        {...flatTree.getTreeProps()}
        aria-label={t('files.treeDescription')}
        className={styles.treeContainer}>
        {Array.from(flatTree.items(), (flatTreeItem) => {
          const { title, version, createdAt, updatedAt, documentUrl, ...treeItemProps } =
            flatTreeItem.getTreeItemProps()
          const isSelected = selectedIds.includes(flatTreeItem.value.toString())

          // Top level item with file name and actions
          if (treeItemProps.value === items[0].value) {
            return (
              <FlatTreeItem
                key={flatTreeItem.value}
                {...treeItemProps}
                className={mergeClasses(
                  styles.fileRow,
                  isSelected && styles.treeItemSelected,
                  isExpanded && styles.fileRowExpanded
                )}
                onOpenChange={() => setIsExpanded((prev) => !prev)}
                itemType="branch">
                <TreeItemLayout
                  className={mergeClasses(styles.treeItemLayout, styles.noIconOverride)}>
                  <CardHeader
                    className={mergeClasses(styles.cardHeader, styles.noLeftPadding)}
                    header={
                      <Body1
                        as="p"
                        className={mergeClasses(styles.title, isSelected && styles.titleSelected)}>
                        {title}
                      </Body1>
                    }
                  />
                </TreeItemLayout>
              </FlatTreeItem>
            )
          }

          const itemValue = flatTreeItem.value.toString()

          // Child items
          return (
            <Tooltip
              key={flatTreeItem.value}
              content={{
                children: (
                  <FileCardTooltipContent
                    name={title}
                    version={version}
                    createdAt={createdAt}
                    updatedAt={updatedAt}
                  />
                ),
                className: tooltipContentClassName,
              }}
              showDelay={0}
              visible={visibleTooltip === itemValue}
              relationship="description"
              appearance="inverted">
              <FlatTreeItem
                key={flatTreeItem.value}
                {...treeItemProps}
                onMouseEnter={() => handleMouseEnter(itemValue)}
                onMouseLeave={() => handleMouseLeave(itemValue)}
                onFocus={() => handleFocus(itemValue)}
                onBlur={() => handleBlur(itemValue)}
                className={mergeClasses(
                  styles.treeChildBase,
                  isSelected && styles.treeItemSelected
                )}>
                <TreeItemLayout
                  className={mergeClasses(styles.treeItemLayout, styles.noIconOverride)}>
                  <Text
                    className={styles.braveTitle}
                    wrap={false}
                    weight={isSelected ? 'semibold' : 'regular'}>
                    {title}
                  </Text>
                  {documentUrl ? (
                    <div
                      className={mergeClasses(
                        'section-action-slot', // Stable class for the parent hover/focus selector in treeChildBase
                        styles.sectionActionSlot,
                        openSectionMenuId === itemValue && styles.sectionActionSlotVisible
                      )}>
                      <Menu
                        onOpenChange={(_, data) => {
                          setOpenSectionMenuId(data.open ? itemValue : null)
                          if (data.open) {
                            clearTooltip()
                          } else {
                            // Defer so it runs after Fluent UI returns focus to the trigger button
                            setTimeout(clearTooltip, 0)
                          }
                        }}>
                        <MenuTrigger disableButtonEnhancement>
                          <Button
                            aria-label={t('files.actions.moreActionsLabel')}
                            appearance="transparent"
                            icon={<MoreHorizontal16Regular />}
                            className={mergeClasses(
                              styles.moreActionsButton,
                              openSectionMenuId === itemValue && styles.moreActionsButtonVisible
                            )}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </MenuTrigger>
                        <MenuPopover>
                          <MenuList>
                            <MenuItem
                              className={styles.menuItem}
                              icon={<Eye16Regular />}
                              secondaryContent={
                                <Open16Regular className={styles.menuItemSecondaryIcon} />
                              }
                              onClick={(e) => {
                                e.stopPropagation()
                                window.open(documentUrl, '_blank', 'noopener,noreferrer')
                              }}>
                              {t('files.actions.previewInBrave')}
                            </MenuItem>
                          </MenuList>
                        </MenuPopover>
                      </Menu>
                    </div>
                  ) : undefined}
                </TreeItemLayout>
              </FlatTreeItem>
            </Tooltip>
          )
        })}
      </FlatTree>
    </Card>
  )
}

export { BraveFileCard }
